import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { PaymentModuleId } from '@triedge/domain-core';
import {
  BanquestControlPlaneGuide,
  StripeControlPlaneGuide,
} from '@triedge/payments';
import { AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { useSettingsBundle } from '../../context/SettingsBundleContext';
import type {
  PaymentControlPlaneReadiness,
  PaymentOnboardingStatus,
  UpdatePaymentControlPlaneInput,
} from '../../types';
import { resolveSettingsOperatorPersona } from '../../utils/operatorPersona';
import {
  Button,
  Card,
  DetailRow,
  formatBooleanValue,
  formatTimestamp,
  ReadinessBanner,
  readText,
  renderUrlValue,
  SectionHeader,
} from './integrationsShared';

type PaymentSectionMode = 'summary' | 'advanced';

type PaymentReadinessStatus = PaymentControlPlaneReadiness['status'];
type PaymentIssueSeverity =
  PaymentControlPlaneReadiness['issues'][number]['severity'];
type DonationFundingMethod =
  PaymentControlPlaneReadiness['sola']['primaryDonationFundingMethod'];
type PaymentReadinessWithDonationsFunding = PaymentControlPlaneReadiness & {
  donationsFundingReadiness?: {
    oneTimePrimaryFundingMethod: DonationFundingMethod;
    oneTimeFallbackFundingMethod: DonationFundingMethod;
    recurringFundingMethod: DonationFundingMethod;
  };
};
type PublicTuitionPaymentMethodType =
  PaymentControlPlaneReadiness['publicTuition']['paymentMethodPolicy']['allowedPaymentMethodTypes'][number];
type PublicTuitionPaymentMethodPolicy =
  PaymentControlPlaneReadiness['publicTuition']['paymentMethodPolicy'];

const PUBLIC_TUITION_PAYMENT_METHOD_OPTIONS: Array<{
  method: PublicTuitionPaymentMethodType;
  label: string;
  description: string;
}> = [
  {
    method: 'card',
    label: 'Cards',
    description: 'Accept credit and debit cards through the secure Stripe payment form.',
  },
  {
    method: 'us_bank_account',
    label: 'ACH bank accounts',
    description: 'Allow US bank-account payments when Stripe and school policy permit it.',
  },
  {
    method: 'cashapp',
    label: 'Cash App Pay',
    description: 'Allow Cash App Pay in sandbox/demo or for production schools that enable it.',
  },
  {
    method: 'klarna',
    label: 'Klarna',
    description: 'Allow Klarna where Stripe supports it and the school chooses to offer it.',
  },
];

const normalizePublicTuitionPaymentMethodTypes = (
  value: readonly PublicTuitionPaymentMethodType[] | null | undefined,
): PublicTuitionPaymentMethodType[] => {
  const validMethods = new Set(PUBLIC_TUITION_PAYMENT_METHOD_OPTIONS.map((option) => option.method));
  const normalized = (Array.isArray(value) ? value : [])
    .filter((method): method is PublicTuitionPaymentMethodType => validMethods.has(method));
  return normalized.length > 0 ? [...new Set(normalized)] : ['card'];
};

const resolvePublicTuitionPaymentMethodPolicy = (
  policy: PublicTuitionPaymentMethodPolicy | null | undefined,
): PublicTuitionPaymentMethodPolicy => ({
  automaticPaymentMethodsEnabled:
    policy?.automaticPaymentMethodsEnabled === true,
  allowedPaymentMethodTypes: normalizePublicTuitionPaymentMethodTypes(
    policy?.allowedPaymentMethodTypes,
  ),
});

const paymentMethodTypesEqual = (
  left: readonly PublicTuitionPaymentMethodType[] | null | undefined,
  right: readonly PublicTuitionPaymentMethodType[] | null | undefined,
): boolean => {
  const normalizedLeft = normalizePublicTuitionPaymentMethodTypes(left).sort();
  const normalizedRight = normalizePublicTuitionPaymentMethodTypes(right).sort();
  return normalizedLeft.length === normalizedRight.length
    && normalizedLeft.every((method, index) => method === normalizedRight[index]);
};

const formatPublicTuitionPaymentMethods = (
  methods: readonly PublicTuitionPaymentMethodType[],
): string =>
  normalizePublicTuitionPaymentMethodTypes(methods)
    .map((method) =>
      PUBLIC_TUITION_PAYMENT_METHOD_OPTIONS.find((option) => option.method === method)?.label ?? method
    )
    .join(', ');

const formatPublicTuitionPaymentMethodPolicy = (
  policy: PublicTuitionPaymentMethodPolicy,
): string =>
  policy.automaticPaymentMethodsEnabled
    ? 'Managed by Stripe'
    : formatPublicTuitionPaymentMethods(policy.allowedPaymentMethodTypes);

const REUSABLE_PUBLIC_TUITION_PAYMENT_METHOD_TYPES = new Set<PublicTuitionPaymentMethodType>([
  'card',
  'us_bank_account',
]);

const hasReusablePublicTuitionPaymentMethod = (
  methods: readonly PublicTuitionPaymentMethodType[],
): boolean =>
  normalizePublicTuitionPaymentMethodTypes(methods).some((method) =>
    REUSABLE_PUBLIC_TUITION_PAYMENT_METHOD_TYPES.has(method),
  );

const formatPaymentReadinessStatus = (
  status: PaymentReadinessStatus,
): string => {
  switch (status) {
    case 'live_ready':
      return 'Live-ready';
    case 'test_ready':
      return 'Test-ready';
    default:
      return 'Not ready';
  }
};

const toPaymentReadinessBannerState = (
  status: PaymentReadinessStatus,
): 'healthy' | 'warning' | 'blocker' => {
  switch (status) {
    case 'live_ready':
      return 'healthy';
    case 'test_ready':
      return 'warning';
    default:
      return 'blocker';
  }
};

const formatIssueSeverity = (severity: PaymentIssueSeverity): string => {
  switch (severity) {
    case 'warning':
      return 'Warning';
    case 'info':
      return 'Info';
    default:
      return 'Error';
  }
};

const issueSeverityClasses = (severity: PaymentIssueSeverity): string => {
  switch (severity) {
    case 'warning':
      return 'border-coral bg-warning-background text-irongrey';
    case 'info':
      return 'border-primary/20 bg-info-background text-primary';
    default:
      return 'border-strawberry/25 bg-error-background text-strawberry';
  }
};

const formatSecretMode = (
  value: PaymentControlPlaneReadiness['runtime']['stripeSecretMode'],
): string => {
  switch (value) {
    case 'live':
      return 'Live payments';
    case 'test':
      return 'Test payments';
    default:
      return 'Unknown';
  }
};

const formatChargeStrategy = (
  value: PaymentControlPlaneReadiness['controlPlane']['chargeStrategy'],
): string => {
  switch (String(value || '').toLowerCase()) {
    case 'direct':
      return 'School Stripe account';
    case 'destination':
    case 'destination_charges':
      return 'Platform-managed school account';
    case 'platform':
      return 'Intellio platform account';
    default:
      return 'Configured by account administrator';
  }
};

const sanitizePaymentIssueMessage = (
  issue: PaymentControlPlaneReadiness['issues'][number],
): string => {
  const rawMessage = readText(issue.message, '');
  const rawCode = readText(issue.code, '');
  if (
    /FieldUndefined|getPaymentControlPlaneReadiness|Validation error|Query/i.test(rawMessage)
    || /readiness-unavailable/i.test(rawCode)
  ) {
    return 'Payment setup could not be checked right now. Refresh this page or ask an account administrator to confirm the payment setup.';
  }
  return rawMessage || 'Payment setup needs attention before parents can pay online.';
};

const isSeededPaymentEvent = (
  event: PaymentControlPlaneReadiness['latestMirrorEvent'],
): boolean =>
  /^evt_demo/i.test(String(event.providerEventId ?? ''))
    || /^demo\./i.test(String(event.eventType ?? ''));

const formatLatestPaymentEventValue = (
  event: PaymentControlPlaneReadiness['latestMirrorEvent'],
  value: string | null | undefined,
  fallback = 'No verified Stripe event recorded',
): string =>
  isSeededPaymentEvent(event) ? 'No verified Stripe event recorded' : readText(value, fallback);

const PaymentIssuesPanel = ({
  issues,
}: {
  issues: PaymentControlPlaneReadiness['issues'];
}) => (
  <div className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-3">
    <div className="flex items-center justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
        Setup Items To Review
      </p>
      <span
        className="text-xs font-semibold text-text-secondary"
        data-testid="settings-integrations-payments-issues-count"
      >
        {issues.length}
      </span>
    </div>
    {issues.length ? (
      <ul
        className="mt-3 space-y-2"
        data-testid="settings-integrations-payments-issues-list"
      >
        {issues.map((issue) => (
          <li
            key={`${issue.code}:${issue.message}`}
            className="rounded-md border border-border bg-muted px-3 py-3"
          >
            <div className="flex items-start gap-3">
              {issue.severity === 'warning' ? (
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-irongrey" />
              ) : (
                <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-strawberry" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span
                    className={`rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${issueSeverityClasses(issue.severity)}`}
                  >
                    {formatIssueSeverity(issue.severity)}
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-text-secondary">
                    Payment setup
                  </span>
                </div>
                <p className="mt-2 text-sm text-irongrey">
                  {sanitizePaymentIssueMessage(issue)}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    ) : (
      <div
        className="mt-3 flex items-center gap-3 rounded-lg bg-success-background px-4 py-3"
        data-testid="settings-integrations-payments-issues-empty"
      >
        <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-jungle" />
        <p className="text-sm font-medium text-jungle">
          All readiness checks passed. No blockers or warnings reported.
        </p>
      </div>
    )}
  </div>
);

const LaunchControlCheckbox = ({
  checked,
  description,
  disabled,
  label,
  onChange,
  testId,
}: {
  checked: boolean;
  description: string;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
  testId: string;
}) => (
  <label className="flex items-start gap-3 rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-2">
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.checked)}
      className="mt-1 h-4 w-4 rounded border-[#9CA3AF] text-[#237062] focus:ring-[#2697a2]"
      data-testid={testId}
    />
    <span>
      <span className="block text-sm font-semibold text-[#111827]">{label}</span>
      <span className="mt-1 block text-xs leading-5 text-text-secondary">
        {description}
      </span>
    </span>
  </label>
);

const PaymentMethodStatusCard = ({
  generalRoute,
  compact,
}: {
  generalRoute: string;
  compact: boolean;
}) => {
  const navigate = useNavigate();
  return (
    <div
      className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-3"
      data-testid="settings-integrations-payments-pmc-relocated-card"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
        Payment methods
      </p>
      <p className="mt-2 text-sm text-[#374151]">
        Payment methods are managed in General settings so checkout options stay separate from
        payout setup and readiness checks.
      </p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(generalRoute)}
          data-testid="settings-integrations-payments-open-general-button"
        >
          Open General settings
        </Button>
        <p className="text-xs text-text-secondary">
          {compact
            ? 'Use General to see which payment methods parents can use.'
            : 'Use General to manage which payment methods parents can use.'}
        </p>
      </div>
    </div>
  );
};

export const PaymentsControlPlaneSection = ({
  mode = 'advanced',
  routePath,
}: {
  mode?: PaymentSectionMode;
  routePath?: string;
}) => {
  const bundle = useSettingsBundle();
  const navigate = useNavigate();
  const location = useLocation();
  const generalRoute = bundle.routeManifest.sectionPath('general');
  const resolvedReturnRoute = routePath ?? location.pathname;
  const isSuperAdmin =
    resolveSettingsOperatorPersona(bundle) === 'super-administrator';

  const paymentReadinessQuery =
    bundle.hooks.useGetPaymentControlPlaneReadinessQuery({});
  const [updatePaymentControlPlane, updatePaymentControlPlaneState] =
    bundle.hooks.useUpdatePaymentControlPlaneMutation();
  const [
    startStripeConnectedAccountOnboarding,
    startStripeConnectedAccountOnboardingState,
  ] = bundle.hooks.useStartStripeConnectedAccountOnboardingMutation();
  const [
    completeStripeConnectedAccountOnboarding,
    completeStripeConnectedAccountOnboardingState,
  ] = bundle.hooks.useCompleteStripeConnectedAccountOnboardingMutation();

  const paymentReadiness = paymentReadinessQuery.data as
    | PaymentControlPlaneReadiness
    | undefined;
  const paymentReadinessWithDonationsFunding =
    paymentReadiness as PaymentReadinessWithDonationsFunding | undefined;
  const paymentIssues = paymentReadiness?.issues ?? [];
  const paymentStatus = paymentReadiness?.status ?? 'not_ready';

  const [paymentDefaultStripeAccountId, setPaymentDefaultStripeAccountId] =
    useState('');
  const [paymentOnboardingStatus, setPaymentOnboardingStatus] =
    useState<PaymentOnboardingStatus>('not_started');
  const [paymentConnectedAccountEmail, setPaymentConnectedAccountEmail] =
    useState('');
  const [paymentConnectedAccountCountry, setPaymentConnectedAccountCountry] =
    useState('US');
  const [
    paymentConnectedAccountBusinessType,
    setPaymentConnectedAccountBusinessType,
  ] = useState<'company' | 'individual'>('company');
  const [paymentSolaPublicIfieldsKey, setPaymentSolaPublicIfieldsKey] =
    useState('');
  const [
    paymentPrimaryDonationFundingMethod,
    setPaymentPrimaryDonationFundingMethod,
  ] = useState<DonationFundingMethod>('sola_daf');
  const [
    paymentFallbackDonationFundingMethod,
    setPaymentFallbackDonationFundingMethod,
  ] = useState<DonationFundingMethod>('stripe_card');
  const [
    paymentRecurringDonationFundingMethod,
    setPaymentRecurringDonationFundingMethod,
  ] = useState<DonationFundingMethod>('stripe_card');
  const [paymentOperationMessage, setPaymentOperationMessage] = useState('');
  const [paymentOperationError, setPaymentOperationError] = useState('');
  const [paymentConnectMessage, setPaymentConnectMessage] = useState('');
  const [paymentConnectError, setPaymentConnectError] = useState('');
  const [banquestEnabled, setBanquestEnabled] = useState(false);
  const [banquestRegistrationMode, setBanquestRegistrationMode] = useState<
    'api_managed' | 'manual'
  >('manual');
  const [banquestAllowedImportModules, setBanquestAllowedImportModules] =
    useState<PaymentModuleId[]>([]);
  const [banquestOperationMessage, setBanquestOperationMessage] = useState('');
  const [banquestOperationError, setBanquestOperationError] = useState('');
  const [
    publicTuitionSavedMethodsEnabled,
    setPublicTuitionSavedMethodsEnabled,
  ] = useState(false);
  const [publicTuitionAutoPayEnabled, setPublicTuitionAutoPayEnabled] =
    useState(false);
  const [
    publicTuitionAutoPaySweepKillSwitch,
    setPublicTuitionAutoPaySweepKillSwitch,
  ] = useState(true);
  const [
    publicTuitionWebhookReconciliationReady,
    setPublicTuitionWebhookReconciliationReady,
  ] = useState(false);
  const [
    publicTuitionTransactionalEmailReady,
    setPublicTuitionTransactionalEmailReady,
  ] = useState(false);
  const [publicTuitionLaunchQaApproved, setPublicTuitionLaunchQaApproved] =
    useState(false);
  const [
    publicTuitionAutomaticPaymentMethodsEnabled,
    setPublicTuitionAutomaticPaymentMethodsEnabled,
  ] = useState(false);
  const [
    publicTuitionAllowedPaymentMethodTypes,
    setPublicTuitionAllowedPaymentMethodTypes,
  ] = useState<PublicTuitionPaymentMethodType[]>(['card']);
  const [publicTuitionOperationMessage, setPublicTuitionOperationMessage] =
    useState('');
  const [publicTuitionOperationError, setPublicTuitionOperationError] =
    useState('');

  const paymentFormDirty = Boolean(
    paymentReadiness &&
      (readText(paymentDefaultStripeAccountId) !==
        readText(paymentReadiness.controlPlane.defaultStripeAccountId) ||
        paymentOnboardingStatus !==
          paymentReadiness.controlPlane.onboardingStatus ||
        readText(paymentSolaPublicIfieldsKey) !==
          readText(paymentReadiness.sola.publicIfieldsKey) ||
        paymentPrimaryDonationFundingMethod !==
          (paymentReadinessWithDonationsFunding?.donationsFundingReadiness
            ?.oneTimePrimaryFundingMethod ??
            paymentReadiness.sola.primaryDonationFundingMethod) ||
        paymentFallbackDonationFundingMethod !==
          (paymentReadinessWithDonationsFunding?.donationsFundingReadiness
            ?.oneTimeFallbackFundingMethod ??
            paymentReadiness.sola.fallbackDonationFundingMethod) ||
        paymentRecurringDonationFundingMethod !==
          (paymentReadinessWithDonationsFunding?.donationsFundingReadiness
            ?.recurringFundingMethod ?? 'stripe_card')),
  );
  const paymentSaveDisabled =
    updatePaymentControlPlaneState.isLoading || !paymentFormDirty;
  const publicTuitionStoredBaseline =
    paymentReadiness?.publicTuitionStored ?? null;
  const publicTuitionResolvedPaymentMethodPolicy =
    resolvePublicTuitionPaymentMethodPolicy(
      paymentReadiness?.publicTuition.paymentMethodPolicy,
    );
  const shouldSavePublicTuitionValue = (
    storedValue: boolean | null | undefined,
    resolvedValue: boolean | undefined,
    currentValue: boolean,
  ): boolean => {
    if (typeof resolvedValue !== 'boolean') {
      return false;
    }
    const baseline = typeof storedValue === 'boolean' ? storedValue : resolvedValue;
    return currentValue !== baseline;
  };
  const shouldSavePublicTuitionPaymentMethodTypes = (
    storedValue: readonly PublicTuitionPaymentMethodType[] | null | undefined,
    resolvedValue: readonly PublicTuitionPaymentMethodType[] | undefined,
    currentValue: readonly PublicTuitionPaymentMethodType[],
  ): boolean => {
    const baseline = Array.isArray(storedValue) ? storedValue : resolvedValue;
    return !paymentMethodTypesEqual(baseline, currentValue);
  };
  const publicTuitionFormDirty = Boolean(
    paymentReadiness &&
      (shouldSavePublicTuitionValue(
        publicTuitionStoredBaseline?.savedMethodsEnabled,
        paymentReadiness.publicTuition.savedMethodsEnabled,
        publicTuitionSavedMethodsEnabled,
      ) ||
        shouldSavePublicTuitionValue(
          publicTuitionStoredBaseline?.autoPayEnabled,
          paymentReadiness.publicTuition.autoPayEnabled,
          publicTuitionAutoPayEnabled,
        ) ||
        shouldSavePublicTuitionValue(
          publicTuitionStoredBaseline?.autoPaySweepKillSwitch,
          paymentReadiness.publicTuition.autoPaySweepKillSwitch,
          publicTuitionAutoPaySweepKillSwitch,
        ) ||
        shouldSavePublicTuitionValue(
          publicTuitionStoredBaseline?.webhookReconciliationReady,
          paymentReadiness.publicTuition.webhookReconciliationReady,
          publicTuitionWebhookReconciliationReady,
        ) ||
        shouldSavePublicTuitionValue(
          publicTuitionStoredBaseline?.transactionalEmailReady,
          paymentReadiness.publicTuition.transactionalEmailReady,
          publicTuitionTransactionalEmailReady,
        ) ||
        shouldSavePublicTuitionValue(
          publicTuitionStoredBaseline?.launchQaApproved,
          paymentReadiness.publicTuition.launchQaApproved,
          publicTuitionLaunchQaApproved,
        ) ||
        shouldSavePublicTuitionValue(
          publicTuitionStoredBaseline?.paymentMethodPolicy?.automaticPaymentMethodsEnabled,
          publicTuitionResolvedPaymentMethodPolicy.automaticPaymentMethodsEnabled,
          publicTuitionAutomaticPaymentMethodsEnabled,
        ) ||
        shouldSavePublicTuitionPaymentMethodTypes(
          publicTuitionStoredBaseline?.paymentMethodPolicy?.allowedPaymentMethodTypes,
          publicTuitionResolvedPaymentMethodPolicy.allowedPaymentMethodTypes,
          publicTuitionAllowedPaymentMethodTypes,
        )),
  );
  const publicTuitionSavedMethodPolicyInvalid =
    publicTuitionSavedMethodsEnabled &&
    !publicTuitionAutomaticPaymentMethodsEnabled &&
    !hasReusablePublicTuitionPaymentMethod(publicTuitionAllowedPaymentMethodTypes);
  const publicTuitionSaveDisabled =
    updatePaymentControlPlaneState.isLoading ||
    !publicTuitionFormDirty ||
    publicTuitionSavedMethodPolicyInvalid;

  useEffect(() => {
    if (!paymentReadiness) {
      return;
    }
    setPaymentDefaultStripeAccountId(
      paymentReadiness.controlPlane.defaultStripeAccountId ?? '',
    );
    setPaymentOnboardingStatus(paymentReadiness.controlPlane.onboardingStatus);
    setPaymentSolaPublicIfieldsKey(paymentReadiness.sola.publicIfieldsKey ?? '');
    setPaymentPrimaryDonationFundingMethod(
      paymentReadinessWithDonationsFunding?.donationsFundingReadiness
        ?.oneTimePrimaryFundingMethod ??
        paymentReadiness.sola.primaryDonationFundingMethod,
    );
    setPaymentFallbackDonationFundingMethod(
      paymentReadinessWithDonationsFunding?.donationsFundingReadiness
        ?.oneTimeFallbackFundingMethod ??
        paymentReadiness.sola.fallbackDonationFundingMethod,
    );
    setPaymentRecurringDonationFundingMethod('stripe_card');
    setBanquestEnabled(paymentReadiness.banquest.enabled);
    setBanquestRegistrationMode(paymentReadiness.banquest.registrationMode);
    setBanquestAllowedImportModules([
      ...paymentReadiness.banquest.allowedImportModules,
    ]);
    setPublicTuitionSavedMethodsEnabled(
      paymentReadiness.publicTuition.savedMethodsEnabled,
    );
    setPublicTuitionAutoPayEnabled(paymentReadiness.publicTuition.autoPayEnabled);
    setPublicTuitionAutoPaySweepKillSwitch(
      paymentReadiness.publicTuition.autoPaySweepKillSwitch,
    );
    setPublicTuitionWebhookReconciliationReady(
      paymentReadiness.publicTuition.webhookReconciliationReady,
    );
    setPublicTuitionTransactionalEmailReady(
      paymentReadiness.publicTuition.transactionalEmailReady,
    );
    setPublicTuitionLaunchQaApproved(
      paymentReadiness.publicTuition.launchQaApproved,
    );
    setPublicTuitionAutomaticPaymentMethodsEnabled(
      publicTuitionResolvedPaymentMethodPolicy.automaticPaymentMethodsEnabled,
    );
    setPublicTuitionAllowedPaymentMethodTypes(
      normalizePublicTuitionPaymentMethodTypes(
        publicTuitionResolvedPaymentMethodPolicy.allowedPaymentMethodTypes,
      ),
    );
  }, [
    paymentReadiness?.banquest.allowedImportModules,
    paymentReadiness?.banquest.enabled,
    paymentReadiness?.banquest.registrationMode,
    paymentReadiness?.controlPlane.defaultStripeAccountId,
    paymentReadiness?.controlPlane.onboardingStatus,
    paymentReadiness?.publicTuition.autoPayEnabled,
    paymentReadiness?.publicTuition.autoPaySweepKillSwitch,
    paymentReadiness?.publicTuition.launchQaApproved,
    paymentReadiness?.publicTuition.paymentMethodPolicy?.allowedPaymentMethodTypes,
    paymentReadiness?.publicTuition.paymentMethodPolicy?.automaticPaymentMethodsEnabled,
    paymentReadiness?.publicTuition.savedMethodsEnabled,
    paymentReadiness?.publicTuition.transactionalEmailReady,
    paymentReadiness?.publicTuition.webhookReconciliationReady,
    paymentReadiness?.sola.publicIfieldsKey,
    paymentReadiness?.sola.primaryDonationFundingMethod,
    paymentReadiness?.sola.fallbackDonationFundingMethod,
    paymentReadinessWithDonationsFunding?.donationsFundingReadiness
      ?.oneTimePrimaryFundingMethod,
    paymentReadinessWithDonationsFunding?.donationsFundingReadiness
      ?.oneTimeFallbackFundingMethod,
    paymentReadinessWithDonationsFunding?.donationsFundingReadiness
      ?.recurringFundingMethod,
  ]);

  const handlePublicTuitionPaymentMethodToggle = (
    method: PublicTuitionPaymentMethodType,
    enabled: boolean,
  ) => {
    setPublicTuitionAllowedPaymentMethodTypes((current) => {
      if (enabled) {
        return normalizePublicTuitionPaymentMethodTypes([...current, method]);
      }
      const next = current.filter((currentMethod) => currentMethod !== method);
      if (
        publicTuitionSavedMethodsEnabled &&
        REUSABLE_PUBLIC_TUITION_PAYMENT_METHOD_TYPES.has(method) &&
        !hasReusablePublicTuitionPaymentMethod(next)
      ) {
        return normalizePublicTuitionPaymentMethodTypes(current);
      }
      return normalizePublicTuitionPaymentMethodTypes(next);
    });
  };

  const buildStripeConnectCallbackUrl = (
    callbackMode: 'refresh' | 'return',
  ): string => {
    if (typeof window === 'undefined') {
      return 'https://example.com/super-administrator/control-plane/payments';
    }
    const url = new URL(resolvedReturnRoute, window.location.origin);
    url.searchParams.set('stripeConnect', callbackMode);
    return url.toString();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeConnect = params.get('stripeConnect');
    const stripeAccountId = readText(params.get('stripeAccountId'));
    const state = params.get('state');
    const code = params.get('code');
    if (!stripeConnect || state || code) {
      return;
    }

    let cancelled = false;
    const refreshStripeReadiness = async () => {
      setPaymentConnectError('');
      setPaymentConnectMessage(
        stripeConnect === 'return'
          ? 'Returned from Stripe onboarding. Refreshing readiness...'
          : 'Stripe requested a refresh. Reloading payments readiness...',
      );
      if (stripeAccountId) {
        try {
          const result = await completeStripeConnectedAccountOnboarding({
            stripeAccountId,
          }).unwrap();
          if (!cancelled) {
            setPaymentDefaultStripeAccountId(
              readText(result.defaultStripeAccountId),
            );
            setPaymentOnboardingStatus(result.onboardingStatus);
            setPaymentConnectMessage(
              `Stripe connected account ${result.defaultStripeAccountId} synchronized as ${result.onboardingStatus}.`,
            );
          }
        } catch (error) {
          if (!cancelled) {
            setPaymentConnectMessage('');
            setPaymentConnectError(
              `Stripe onboarding sync failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
            );
          }
        }
      }
      await paymentReadinessQuery.refetch();
      if (!cancelled) {
        navigate(resolvedReturnRoute, { replace: true });
      }
    };
    void refreshStripeReadiness();

    return () => {
      cancelled = true;
    };
  }, [
    completeStripeConnectedAccountOnboarding,
    location.search,
    navigate,
    paymentReadinessQuery,
    resolvedReturnRoute,
  ]);

  const handleSavePaymentControlPlane = async () => {
    const normalizedDefaultAccountId =
      readText(paymentDefaultStripeAccountId) || null;
    const normalizedSolaPublicIfieldsKey =
      readText(paymentSolaPublicIfieldsKey) || null;

    if (
      normalizedDefaultAccountId &&
      !normalizedDefaultAccountId.startsWith('acct_')
    ) {
      setPaymentOperationMessage('');
      setPaymentOperationError(
        'Stripe default account ID must start with acct_.',
      );
      return;
    }

    if (
      paymentOnboardingStatus === 'active' &&
      (!normalizedDefaultAccountId ||
        !normalizedDefaultAccountId.startsWith('acct_'))
    ) {
      setPaymentOperationMessage('');
      setPaymentOperationError(
        'Active onboarding requires a real default Stripe account ID.',
      );
      return;
    }

    if (
      paymentPrimaryDonationFundingMethod === paymentFallbackDonationFundingMethod
    ) {
      setPaymentOperationMessage('');
      setPaymentOperationError(
        'Sola donation primary and fallback funding methods must be different.',
      );
      return;
    }

    if (paymentRecurringDonationFundingMethod !== 'stripe_card') {
      setPaymentOperationMessage('');
      setPaymentOperationError(
        'Recurring donations currently require the Stripe card fallback.',
      );
      return;
    }

    try {
      const result = await updatePaymentControlPlane({
        defaultStripeAccountId: normalizedDefaultAccountId,
        onboardingStatus: paymentOnboardingStatus,
        sola: {
          publicIfieldsKey: normalizedSolaPublicIfieldsKey,
          primaryDonationFundingMethod: paymentPrimaryDonationFundingMethod,
          fallbackDonationFundingMethod: paymentFallbackDonationFundingMethod,
        },
        donationsFunding: {
          oneTimePrimaryFundingMethod: paymentPrimaryDonationFundingMethod,
          oneTimeFallbackFundingMethod: paymentFallbackDonationFundingMethod,
          recurringFundingMethod: paymentRecurringDonationFundingMethod,
        },
      }).unwrap();
      setPaymentOperationError('');
      setPaymentOperationMessage(
        `Payment setup saved. Stripe status: ${result.onboardingStatus}. Default account: ${readText(result.defaultStripeAccountId, 'not configured')}. Donations: ${result.donationsFunding.oneTimePrimaryFundingMethod} to ${result.donationsFunding.oneTimeFallbackFundingMethod}. Recurring: ${result.donationsFunding.recurringFundingMethod}.`,
      );
      await paymentReadinessQuery.refetch();
    } catch (error) {
      setPaymentOperationMessage('');
      setPaymentOperationError(
        `Payment setup could not be saved: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleStartStripeConnectOnboarding = async (
    startMode: 'create' | 'link',
  ) => {
    const existingStripeAccountId =
      readText(paymentDefaultStripeAccountId) || null;
    const contactEmail = readText(paymentConnectedAccountEmail) || null;
    const country = readText(
      paymentConnectedAccountCountry,
      'US',
    ).toUpperCase();

    if (!/^[A-Z]{2}$/.test(country)) {
      setPaymentConnectMessage('');
      setPaymentConnectError(
        'Country must be a two-letter ISO code, for example US.',
      );
      return;
    }

    if (startMode === 'link') {
      if (
        !existingStripeAccountId ||
        !existingStripeAccountId.startsWith('acct_')
      ) {
        setPaymentConnectMessage('');
        setPaymentConnectError(
          'Set a real acct_ Stripe account ID before generating an onboarding link for the current account.',
        );
        return;
      }
    } else if (!contactEmail) {
      setPaymentConnectMessage('');
      setPaymentConnectError(
        'Contact email is required when creating a new Stripe connected account.',
      );
      return;
    }

    try {
      const result = await startStripeConnectedAccountOnboarding({
        existingStripeAccountId:
          startMode === 'link' ? existingStripeAccountId : null,
        contactEmail: startMode === 'create' ? contactEmail : null,
        country,
        businessType: paymentConnectedAccountBusinessType,
        refreshUrl: buildStripeConnectCallbackUrl('refresh'),
        returnUrl: buildStripeConnectCallbackUrl('return'),
      }).unwrap();
      setPaymentConnectError('');
      setPaymentDefaultStripeAccountId(result.stripeAccountId);
      setPaymentOnboardingStatus((current) =>
        current === 'active' ? current : 'pending',
      );
      setPaymentConnectMessage(
        result.action === 'created'
          ? `Created Stripe connected account ${result.stripeAccountId}. Complete Stripe onboarding in the new tab, then return here to refresh setup.`
          : `Generated a Stripe onboarding link for ${result.stripeAccountId}. Complete onboarding in the new tab, then return here to refresh setup.`,
      );
      if (typeof window !== 'undefined') {
        window.open(result.onboardingUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      setPaymentConnectMessage('');
      setPaymentConnectError(
        `Stripe onboarding failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleCompleteStripeOnboardingSync = async () => {
    const stripeAccountId = readText(paymentDefaultStripeAccountId);
    if (!stripeAccountId || !stripeAccountId.startsWith('acct_')) {
      setPaymentConnectMessage('');
      setPaymentConnectError(
        'Set a real acct_ Stripe account ID before synchronizing onboarding state.',
      );
      return;
    }

    try {
      const result = await completeStripeConnectedAccountOnboarding({
        stripeAccountId,
      }).unwrap();
      setPaymentConnectError('');
      setPaymentConnectMessage(
        `Stripe connected account ${result.defaultStripeAccountId} synchronized as ${result.onboardingStatus}.`,
      );
      setPaymentOnboardingStatus(result.onboardingStatus);
      await paymentReadinessQuery.refetch();
    } catch (error) {
      setPaymentConnectMessage('');
      setPaymentConnectError(
        `Stripe onboarding sync failed: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleSaveBanquestControlPlane = async () => {
    try {
      const result = await updatePaymentControlPlane({
        banquest: {
          enabled: banquestEnabled,
          registrationMode: banquestRegistrationMode,
          allowedImportModules: [...banquestAllowedImportModules],
        },
      }).unwrap();
      setBanquestOperationError('');
      setBanquestOperationMessage(
        `Banquest settings saved: ${result.banquest.enabled ? 'enabled' : 'disabled'} / ${result.banquest.registrationMode} / ${result.banquest.allowedImportModules.length} modules.`,
      );
      await paymentReadinessQuery.refetch();
    } catch (error) {
      setBanquestOperationMessage('');
      setBanquestOperationError(
        `Banquest settings could not be saved: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  const handleSavePublicTuitionLaunchControls = async () => {
    if (!paymentReadiness) {
      setPublicTuitionOperationMessage('');
      setPublicTuitionOperationError('Parent tuition payment options are not ready to save.');
      return;
    }

    if (publicTuitionSavedMethodPolicyInvalid) {
      setPublicTuitionOperationMessage('');
      setPublicTuitionOperationError(
        'Saved payment methods require at least one reusable Stripe method, such as cards or ACH bank accounts.',
      );
      return;
    }

    const publicTuitionPatch: NonNullable<UpdatePaymentControlPlaneInput['publicTuition']> = {};
    const storedBaseline = paymentReadiness.publicTuitionStored ?? null;
    const resolvedBaseline = paymentReadiness.publicTuition;
    const resolvedBaselinePaymentMethodPolicy =
      resolvePublicTuitionPaymentMethodPolicy(resolvedBaseline.paymentMethodPolicy);
    if (shouldSavePublicTuitionValue(
      storedBaseline?.savedMethodsEnabled,
      resolvedBaseline.savedMethodsEnabled,
      publicTuitionSavedMethodsEnabled,
    )) {
      publicTuitionPatch.savedMethodsEnabled = publicTuitionSavedMethodsEnabled;
    }
    if (shouldSavePublicTuitionValue(
      storedBaseline?.autoPayEnabled,
      resolvedBaseline.autoPayEnabled,
      publicTuitionAutoPayEnabled,
    )) {
      publicTuitionPatch.autoPayEnabled = publicTuitionAutoPayEnabled;
    }
    if (shouldSavePublicTuitionValue(
      storedBaseline?.autoPaySweepKillSwitch,
      resolvedBaseline.autoPaySweepKillSwitch,
      publicTuitionAutoPaySweepKillSwitch,
    )) {
      publicTuitionPatch.autoPaySweepKillSwitch = publicTuitionAutoPaySweepKillSwitch;
    }
    if (shouldSavePublicTuitionValue(
      storedBaseline?.webhookReconciliationReady,
      resolvedBaseline.webhookReconciliationReady,
      publicTuitionWebhookReconciliationReady,
    )) {
      publicTuitionPatch.webhookReconciliationReady = publicTuitionWebhookReconciliationReady;
    }
    if (shouldSavePublicTuitionValue(
      storedBaseline?.transactionalEmailReady,
      resolvedBaseline.transactionalEmailReady,
      publicTuitionTransactionalEmailReady,
    )) {
      publicTuitionPatch.transactionalEmailReady = publicTuitionTransactionalEmailReady;
    }
    if (shouldSavePublicTuitionValue(
      storedBaseline?.launchQaApproved,
      resolvedBaseline.launchQaApproved,
      publicTuitionLaunchQaApproved,
    )) {
      publicTuitionPatch.launchQaApproved = publicTuitionLaunchQaApproved;
    }
    const paymentMethodPolicyChanged = shouldSavePublicTuitionValue(
      storedBaseline?.paymentMethodPolicy?.automaticPaymentMethodsEnabled,
      resolvedBaselinePaymentMethodPolicy.automaticPaymentMethodsEnabled,
      publicTuitionAutomaticPaymentMethodsEnabled,
    ) || shouldSavePublicTuitionPaymentMethodTypes(
      storedBaseline?.paymentMethodPolicy?.allowedPaymentMethodTypes,
      resolvedBaselinePaymentMethodPolicy.allowedPaymentMethodTypes,
      publicTuitionAllowedPaymentMethodTypes,
    );
    if (paymentMethodPolicyChanged) {
      publicTuitionPatch.paymentMethodPolicy = {
        automaticPaymentMethodsEnabled: publicTuitionAutomaticPaymentMethodsEnabled,
        allowedPaymentMethodTypes: normalizePublicTuitionPaymentMethodTypes(
          publicTuitionAllowedPaymentMethodTypes,
        ),
      };
    }
    if (Object.keys(publicTuitionPatch).length === 0) {
      setPublicTuitionOperationError('');
      setPublicTuitionOperationMessage('Parent tuition payment options are already current.');
      return;
    }

    try {
      const result = await updatePaymentControlPlane({
        publicTuition: publicTuitionPatch,
      }).unwrap();
      const resultPaymentMethodPolicy = resolvePublicTuitionPaymentMethodPolicy(
        result.publicTuition.paymentMethodPolicy,
      );
      setPublicTuitionOperationError('');
      setPublicTuitionOperationMessage(
        `Parent tuition payment options saved. Saved methods are ${result.publicTuition.savedMethodsEnabled ? 'on' : 'off'}, auto-pay is ${result.publicTuition.autoPayEnabled ? 'on' : 'off'}, payment methods are ${formatPublicTuitionPaymentMethodPolicy(resultPaymentMethodPolicy)}, and launch review is ${result.publicTuition.launchQaApproved ? 'approved' : 'not approved'}.`,
      );
      await paymentReadinessQuery.refetch();
    } catch (error) {
      setPublicTuitionOperationMessage('');
      setPublicTuitionOperationError(
        `Parent tuition payment options could not be saved: ${readText((error as { message?: unknown })?.message, 'Unknown error')}`,
      );
    }
  };

  if (mode === 'summary') {
    return (
      <Card data-testid="settings-integrations-payments-card">
        <SectionHeader
          title="Online payments"
          description="Review whether parents can pay tuition online and what still needs attention before launch."
          action={(
            <Button
              variant="outline"
              onClick={() => paymentReadinessQuery.refetch()}
              disabled={paymentReadinessQuery.isFetching}
              data-testid="settings-integrations-payments-refresh-button"
            >
              {paymentReadinessQuery.isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
          )}
        />

        {paymentReadiness ? (
          <div className="space-y-4">
            <ReadinessBanner
              state={toPaymentReadinessBannerState(paymentStatus)}
              title={`Payment setup: ${formatPaymentReadinessStatus(paymentStatus)}`}
              description={`Items to fix: ${paymentReadiness.blockingIssueCount} · Items to review: ${paymentReadiness.warningCount} · Mode: ${formatSecretMode(paymentReadiness.runtime.stripeSecretMode)}.`}
              testId="settings-integrations-payments-status-banner"
              className="mb-1"
            />

            <div
              className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3"
              data-testid="settings-integrations-payments-summary-card"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                Payment setup summary
              </p>
              <div className="mt-3">
                <DetailRow
                  label="Stripe account setup"
                  value={paymentReadiness.controlPlane.onboardingStatus}
                  testId="settings-integrations-payments-onboarding-value"
                />
                <DetailRow
                  label="Connected account"
                  value={
                    paymentReadiness.controlPlane.defaultStripeAccountId
                      ? 'Configured'
                      : 'Not configured'
                  }
                  testId="settings-integrations-payments-default-account-value"
                />
                <DetailRow
                  label="Where payments settle"
                  value={formatChargeStrategy(paymentReadiness.controlPlane.chargeStrategy)}
                  testId="settings-integrations-payments-charge-strategy-value"
                />
                <DetailRow
                  label="Saved payment methods"
                  value={formatBooleanValue(
                    paymentReadiness.publicTuition.savedMethodsEnabled,
                  )}
                  testId="settings-integrations-payments-saved-methods-value"
                />
                <DetailRow
                  label="Auto-pay"
                  value={formatBooleanValue(
                    paymentReadiness.publicTuition.autoPayEnabled,
                  )}
                  testId="settings-integrations-payments-auto-pay-value"
                />
                <DetailRow
                  label="Payment Methods"
                  value={formatPublicTuitionPaymentMethodPolicy(publicTuitionResolvedPaymentMethodPolicy)}
                  testId="settings-integrations-payments-public-tuition-methods-value"
                />
                <DetailRow
                  label="Launch review"
                  value={formatBooleanValue(
                    paymentReadiness.publicTuition.launchQaApproved,
                    {
                      trueLabel: 'Approved',
                      falseLabel: 'Not approved',
                    },
                  )}
                  testId="settings-integrations-payments-launch-qa-value"
                />
                <DetailRow
                  label="Last changed"
                  value={formatTimestamp(paymentReadiness.controlPlane.updatedAt)}
                  testId="settings-integrations-payments-updated-at-value"
                />
              </div>
              <p className="mt-3 text-xs text-text-secondary">
                Account administrators manage detailed Stripe setup and launch approval.
              </p>
            </div>

            <PaymentMethodStatusCard generalRoute={generalRoute} compact />
            <PaymentIssuesPanel issues={paymentIssues} />
          </div>
        ) : paymentReadinessQuery.isError ? (
          <p
            className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#B91C1C]"
            data-testid="settings-integrations-payments-load-error"
          >
            Unable to load payments readiness.
          </p>
        ) : (
          <p
            className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm text-text-secondary"
            data-testid="settings-integrations-payments-loading"
          >
            Loading payments readiness...
          </p>
        )}
      </Card>
    );
  }

  return (
    <Card data-testid="control-plane-payments-card">
      <SectionHeader
        title="Payment setup"
        description="Manage Stripe account setup, online payment options, and launch approval for this school."
        action={(
          <Button
            variant="outline"
            onClick={() => paymentReadinessQuery.refetch()}
            disabled={paymentReadinessQuery.isFetching}
            data-testid="control-plane-payments-refresh-button"
          >
            {paymentReadinessQuery.isFetching ? 'Refreshing...' : 'Refresh'}
          </Button>
        )}
      />

      {paymentReadiness ? (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <div className="space-y-4">
            <ReadinessBanner
              state={toPaymentReadinessBannerState(paymentStatus)}
              title={`Payment setup: ${formatPaymentReadinessStatus(paymentStatus)}`}
              description={`Items to fix: ${paymentReadiness.blockingIssueCount} · Items to review: ${paymentReadiness.warningCount} · Mode: ${formatSecretMode(paymentReadiness.runtime.stripeSecretMode)}.`}
              testId="control-plane-payments-status-banner"
              className="mb-1"
            />

            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                School payment settings
              </p>
              <div className="mt-3">
                <DetailRow
                  label="Stripe account setup"
                  value={paymentReadiness.controlPlane.onboardingStatus}
                  testId="control-plane-payments-onboarding-value"
                />
                <DetailRow
                  label="Where payments settle"
                  value={formatChargeStrategy(paymentReadiness.controlPlane.chargeStrategy)}
                  testId="control-plane-payments-charge-strategy-value"
                />
                <DetailRow
                  label="Connected account"
                  value={
                    paymentReadiness.controlPlane.defaultStripeAccountId ??
                    'Not configured'
                  }
                  testId="control-plane-payments-default-account-value"
                />
                <DetailRow
                  label="Custom payment settings"
                  value={formatBooleanValue(
                    paymentReadiness.controlPlane.hasModuleOverrides,
                  )}
                  testId="control-plane-payments-module-overrides-value"
                />
                <DetailRow
                  label="Last changed"
                  value={formatTimestamp(paymentReadiness.controlPlane.updatedAt)}
                  testId="control-plane-payments-updated-at-value"
                />
                <DetailRow
                  label="Changed by"
                  value={paymentReadiness.controlPlane.updatedBy ?? 'Not available'}
                  testId="control-plane-payments-updated-by-value"
                />
              </div>
            </div>

            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                App connection check
              </p>
              <div className="mt-3">
                <DetailRow
                  label="Stripe key"
                  value={formatBooleanValue(
                    paymentReadiness.runtime.hasStripeSecretConfigured,
                  )}
                  testId="control-plane-payments-has-secret"
                />
                <DetailRow
                  label="Stripe webhook key"
                  value={formatBooleanValue(
                    paymentReadiness.runtime.hasWebhookSecretConfigured,
                  )}
                  testId="control-plane-payments-has-webhook-secret"
                />
                <DetailRow
                  label="Success URL"
                  value={renderUrlValue(
                    paymentReadiness.urls.successUrl,
                    'control-plane-payments-success-url-value',
                  )}
                />
                <DetailRow
                  label="Cancel URL"
                  value={renderUrlValue(
                    paymentReadiness.urls.cancelUrl,
                    'control-plane-payments-cancel-url-value',
                  )}
                />
                <DetailRow
                  label="Portal Return"
                  value={renderUrlValue(
                    paymentReadiness.urls.portalReturnUrl,
                    'control-plane-payments-portal-return-url-value',
                  )}
                />
                <DetailRow
                  label="Webhook URL"
                  value={renderUrlValue(
                    paymentReadiness.urls.webhookUrl,
                    'control-plane-payments-webhook-url-value',
                  )}
                />
                <DetailRow
                  label="Webhook Manifest"
                  value={renderUrlValue(
                    paymentReadiness.urls.manifestUrl,
                    'control-plane-payments-webhook-manifest-url-value',
                  )}
                />
              </div>
            </div>

            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                Latest verified Stripe update
              </p>
              <div className="mt-3">
                <DetailRow
                  label="Update type"
                  value={formatLatestPaymentEventValue(
                    paymentReadiness.latestMirrorEvent,
                    paymentReadiness.latestMirrorEvent.eventType,
                  )}
                  testId="control-plane-payments-latest-event-type"
                />
                <DetailRow
                  label="Applies to"
                  value={formatLatestPaymentEventValue(
                    paymentReadiness.latestMirrorEvent,
                    paymentReadiness.latestMirrorEvent.moduleTarget,
                  )}
                  testId="control-plane-payments-latest-event-module"
                />
                <DetailRow
                  label="Occurred"
                  value={isSeededPaymentEvent(paymentReadiness.latestMirrorEvent)
                    ? 'No verified Stripe event recorded'
                    : formatTimestamp(paymentReadiness.latestMirrorEvent.occurredAt)}
                  testId="control-plane-payments-latest-event-occurred-at"
                />
                <DetailRow
                  label="Recorded"
                  value={isSeededPaymentEvent(paymentReadiness.latestMirrorEvent)
                    ? 'No verified Stripe event recorded'
                    : formatTimestamp(paymentReadiness.latestMirrorEvent.recordedAt)}
                  testId="control-plane-payments-latest-event-recorded-at"
                />
                <DetailRow
                  label="Reference"
                  value={formatLatestPaymentEventValue(
                    paymentReadiness.latestMirrorEvent,
                    paymentReadiness.latestMirrorEvent.providerEventId,
                  )}
                  testId="control-plane-payments-latest-event-provider-id"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                Default Stripe account check
              </p>
              <div className="mt-3">
                <DetailRow
                  label="Exists"
                  value={formatBooleanValue(paymentReadiness.stripeAccount.exists)}
                  testId="control-plane-payments-account-exists-value"
                />
                <DetailRow
                  label="Mode"
                  value={formatBooleanValue(paymentReadiness.stripeAccount.livemode, {
                    trueLabel: 'Live',
                    falseLabel: 'Test',
                  })}
                  testId="control-plane-payments-account-mode-value"
                />
                <DetailRow
                  label="Charges Enabled"
                  value={formatBooleanValue(paymentReadiness.stripeAccount.chargesEnabled)}
                  testId="control-plane-payments-account-charges-value"
                />
                <DetailRow
                  label="Payouts Enabled"
                  value={formatBooleanValue(paymentReadiness.stripeAccount.payoutsEnabled)}
                  testId="control-plane-payments-account-payouts-value"
                />
                <DetailRow
                  label="Details Submitted"
                  value={formatBooleanValue(paymentReadiness.stripeAccount.detailsSubmitted)}
                  testId="control-plane-payments-account-details-value"
                />
                <DetailRow
                  label="Display Name"
                  value={paymentReadiness.stripeAccount.displayName ?? 'Not available'}
                  testId="control-plane-payments-account-display-name"
                />
                <DetailRow
                  label="Email"
                  value={paymentReadiness.stripeAccount.email ?? 'Not available'}
                  testId="control-plane-payments-account-email"
                />
                <DetailRow
                  label="Country"
                  value={paymentReadiness.stripeAccount.country ?? 'Not available'}
                  testId="control-plane-payments-account-country"
                />
              </div>
              {paymentReadiness.stripeAccount.errorMessage ? (
                <p
                  className="mt-3 text-xs text-[#B91C1C]"
                  data-testid="control-plane-payments-account-error"
                >
                  {paymentReadiness.stripeAccount.errorMessage}
                </p>
              ) : null}
            </div>

            <div className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-3">
              <StripeControlPlaneGuide
                readiness={paymentReadiness}
                form={{
                  defaultStripeAccountId: paymentDefaultStripeAccountId,
                  onboardingStatus: paymentOnboardingStatus,
                  contactEmail: paymentConnectedAccountEmail,
                  country: paymentConnectedAccountCountry,
                  businessType: paymentConnectedAccountBusinessType,
                }}
                actions={{
                  isSaving:
                    updatePaymentControlPlaneState.isLoading &&
                    paymentSaveDisabled === false,
                  isStartingOnboarding:
                    startStripeConnectedAccountOnboardingState.isLoading,
                  isSyncing:
                    completeStripeConnectedAccountOnboardingState.isLoading,
                }}
                messages={{
                  operationMessage: paymentOperationMessage,
                  operationError: paymentOperationError,
                  connectMessage: paymentConnectMessage,
                  connectError: paymentConnectError,
                }}
                onDefaultStripeAccountIdChange={(value) =>
                  setPaymentDefaultStripeAccountId(value)
                }
                onOnboardingStatusChange={(value) =>
                  setPaymentOnboardingStatus(value)
                }
                onContactEmailChange={(value) =>
                  setPaymentConnectedAccountEmail(value)
                }
                onCountryChange={(value) =>
                  setPaymentConnectedAccountCountry(value.toUpperCase())
                }
                onBusinessTypeChange={(value) =>
                  setPaymentConnectedAccountBusinessType(value)
                }
                onStartCreate={() =>
                  void handleStartStripeConnectOnboarding('create')
                }
                onStartLink={() =>
                  void handleStartStripeConnectOnboarding('link')
                }
                onSync={() => void handleCompleteStripeOnboardingSync()}
                onSave={() => void handleSavePaymentControlPlane()}
              />
            </div>

            {isSuperAdmin ? (
              <>
                <div
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-3"
                  data-testid="control-plane-payments-public-tuition-card"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                        Parent tuition payment options
                      </p>
                      <p className="mt-2 text-sm text-[#374151]">
                        Choose which payment features parents can use for this school.
                      </p>
                    </div>
                    <span
                      className={`rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                        paymentReadiness.publicTuition.launchQaApproved
                          ? 'border-[#BBF7D0] bg-[#F0FDF4] text-[#166534]'
                          : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280]'
                      }`}
                      data-testid="control-plane-payments-public-tuition-qa-badge"
                    >
                      {paymentReadiness.publicTuition.launchQaApproved
                        ? 'Approved'
                        : 'Needs review'}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3">
                    <LaunchControlCheckbox
                      checked={publicTuitionSavedMethodsEnabled}
                      disabled={updatePaymentControlPlaneState.isLoading}
                      label="Saved payment methods"
                      description="Let parents save an eligible Stripe payment method for future tuition payments."
                      testId="control-plane-payments-public-tuition-saved-methods"
                      onChange={setPublicTuitionSavedMethodsEnabled}
                    />
                    <div
                      className="rounded-md border border-[#E5E7EB] bg-[#F9FAFB] px-3 py-3"
                      data-testid="control-plane-payments-public-tuition-method-policy"
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-[#111827]">
                            Parent payment methods
                          </p>
                          <p className="mt-1 text-xs leading-5 text-text-secondary">
                            Choose which Stripe-supported methods parents can see in
                            public tuition checkout. Production schools should start
                            with the safest set and expand per school.
                          </p>
                        </div>
                        <span
                          className="rounded-full border border-[#D1D5DB] bg-white px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#4B5563]"
                          data-testid="control-plane-payments-public-tuition-method-summary"
                        >
                          {publicTuitionAutomaticPaymentMethodsEnabled
                            ? 'Managed by Stripe'
                            : formatPublicTuitionPaymentMethods(publicTuitionAllowedPaymentMethodTypes)}
                        </span>
                      </div>

                      <div className="mt-3">
                        <LaunchControlCheckbox
                          checked={publicTuitionAutomaticPaymentMethodsEnabled}
                          disabled={updatePaymentControlPlaneState.isLoading}
                          label="Let Stripe manage eligible payment methods"
                          description="Stripe will show the methods that are eligible for this school and payment setup."
                          testId="control-plane-payments-public-tuition-automatic-methods"
                          onChange={setPublicTuitionAutomaticPaymentMethodsEnabled}
                        />
                      </div>

                      <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                        {PUBLIC_TUITION_PAYMENT_METHOD_OPTIONS.map((option) => {
                          const checked = publicTuitionAllowedPaymentMethodTypes.includes(option.method);
                          const isLastExplicitMethod =
                            checked && publicTuitionAllowedPaymentMethodTypes.length === 1;
                          const isLastReusableMethod =
                            checked &&
                            REUSABLE_PUBLIC_TUITION_PAYMENT_METHOD_TYPES.has(option.method) &&
                            !hasReusablePublicTuitionPaymentMethod(
                              publicTuitionAllowedPaymentMethodTypes.filter(
                                (method) => method !== option.method,
                              ),
                            );
                          return (
                            <LaunchControlCheckbox
                              key={option.method}
                              checked={checked}
                              disabled={
                                updatePaymentControlPlaneState.isLoading
                                  || publicTuitionAutomaticPaymentMethodsEnabled
                                  || isLastExplicitMethod
                                  || (publicTuitionSavedMethodsEnabled && isLastReusableMethod)
                              }
                              label={option.label}
                              description={option.description}
                              testId={`control-plane-payments-public-tuition-method-${option.method}`}
                              onChange={(enabled) =>
                                handlePublicTuitionPaymentMethodToggle(option.method, enabled)
                              }
                            />
                          );
                        })}
                      </div>
                      {publicTuitionSavedMethodPolicyInvalid ? (
                        <p
                          className="mt-3 rounded-md border border-[#FCA5A5] bg-[#FEF2F2] px-3 py-2 text-xs font-semibold text-[#B91C1C]"
                          data-testid="control-plane-payments-public-tuition-method-policy-warning"
                        >
                          Saved payment methods require cards or ACH bank accounts
                          unless Stripe is managing eligible payment methods.
                        </p>
                      ) : null}
                    </div>
                    <LaunchControlCheckbox
                      checked={publicTuitionAutoPayEnabled}
                      disabled={updatePaymentControlPlaneState.isLoading}
                      label="Auto-pay"
                      description="Allow scheduled tuition charges after saved payment methods are fully ready."
                      testId="control-plane-payments-public-tuition-auto-pay"
                      onChange={setPublicTuitionAutoPayEnabled}
                    />
                    <LaunchControlCheckbox
                      checked={publicTuitionAutoPaySweepKillSwitch}
                      disabled={updatePaymentControlPlaneState.isLoading}
                      label="Keep scheduled auto-pay paused"
                      description="Keep scheduled auto-pay off until an account administrator turns it on."
                      testId="control-plane-payments-public-tuition-sweep-kill-switch"
                      onChange={setPublicTuitionAutoPaySweepKillSwitch}
                    />
                    <LaunchControlCheckbox
                      checked={publicTuitionWebhookReconciliationReady}
                      disabled={updatePaymentControlPlaneState.isLoading}
                      label="Stripe payment updates ready"
                      description="Confirm that Stripe payment updates are reaching the tuition ledger correctly."
                      testId="control-plane-payments-public-tuition-webhook-ready"
                      onChange={setPublicTuitionWebhookReconciliationReady}
                    />
                    <LaunchControlCheckbox
                      checked={publicTuitionTransactionalEmailReady}
                      disabled={updatePaymentControlPlaneState.isLoading}
                      label="Parent email ready"
                      description="Confirm that magic-link and receipt emails have been reviewed."
                      testId="control-plane-payments-public-tuition-email-ready"
                      onChange={setPublicTuitionTransactionalEmailReady}
                    />
                    <LaunchControlCheckbox
                      checked={publicTuitionLaunchQaApproved}
                      disabled={updatePaymentControlPlaneState.isLoading}
                      label="Launch review approved"
                      description="Approve this school's parent payment setup after the hosted payment journey passes."
                      testId="control-plane-payments-public-tuition-launch-qa"
                      onChange={setPublicTuitionLaunchQaApproved}
                    />
                  </div>

                  <div className="mt-4 flex items-center justify-between gap-3">
                    <div className="min-h-[1.25rem] text-sm">
                      {publicTuitionOperationError ? (
                        <p
                          className="font-semibold text-[#B91C1C]"
                          data-testid="control-plane-payments-public-tuition-error"
                        >
                          {publicTuitionOperationError}
                        </p>
                      ) : publicTuitionOperationMessage ? (
                        <p
                          className="font-semibold text-[#166534]"
                          data-testid="control-plane-payments-public-tuition-message"
                        >
                          {publicTuitionOperationMessage}
                        </p>
                      ) : null}
                    </div>
                    <Button
                      onClick={() => void handleSavePublicTuitionLaunchControls()}
                      disabled={publicTuitionSaveDisabled}
                      data-testid="control-plane-payments-public-tuition-save"
                    >
                      {updatePaymentControlPlaneState.isLoading
                        ? 'Saving...'
                        : 'Save payment options'}
                    </Button>
                  </div>
                </div>

                <div
                  className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-3"
                  data-testid="control-plane-payments-banquest-card"
                >
                  <BanquestControlPlaneGuide
                    readiness={paymentReadiness}
                    form={{
                      enabled: banquestEnabled,
                      registrationMode: banquestRegistrationMode,
                      allowedImportModules: banquestAllowedImportModules,
                    }}
                    actions={{
                      isSaving: updatePaymentControlPlaneState.isLoading,
                    }}
                    messages={{
                      operationMessage: banquestOperationMessage,
                      operationError: banquestOperationError,
                    }}
                    onEnabledChange={(value) => setBanquestEnabled(value)}
                    onRegistrationModeChange={(value) =>
                      setBanquestRegistrationMode(value)
                    }
                    onAllowedImportModuleToggle={(moduleId, enabled) =>
                      setBanquestAllowedImportModules((current) =>
                        enabled
                          ? [...current, moduleId].filter(
                              (value, index, array) =>
                                array.indexOf(value) === index,
                            )
                          : current.filter((value) => value !== moduleId),
                      )
                    }
                    onSave={() => void handleSaveBanquestControlPlane()}
                  />
                </div>
              </>
            ) : null}

            <div
              className="rounded-lg border border-[#E5E7EB] bg-white px-4 py-3"
              data-testid="control-plane-payments-sola-card"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                    Sola Donations Funding
                  </p>
                  <p className="mt-2 text-sm text-[#374151]">
                    Sola DAF is the primary donations lane for this wave.
                    Stripe remains the hosted card fallback and the only
                    recurring path.
                  </p>
                </div>
                <span
                  className={`rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
                    paymentReadiness.sola.enabled &&
                    paymentReadiness.sola.donationsEnabled
                      ? 'border-[#BBF7D0] bg-[#F0FDF4] text-[#166534]'
                      : 'border-[#E5E7EB] bg-[#F9FAFB] text-[#6B7280]'
                  }`}
                >
                  {paymentReadiness.sola.enabled &&
                  paymentReadiness.sola.donationsEnabled
                    ? 'Enabled'
                    : 'Disabled'}
                </span>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-3">
                  <input
                    aria-label="Sola Public iFields Key"
                    value={paymentSolaPublicIfieldsKey}
                    onChange={(event) =>
                      setPaymentSolaPublicIfieldsKey(event.currentTarget.value)
                    }
                    className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
                    data-testid="control-plane-payments-sola-public-key-input"
                  />
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[#111827]">
                      Primary donations funding method
                    </span>
                    <select
                      value={paymentPrimaryDonationFundingMethod}
                      onChange={(event) =>
                        setPaymentPrimaryDonationFundingMethod(
                          event.currentTarget.value as DonationFundingMethod,
                        )
                      }
                      className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
                      data-testid="control-plane-payments-sola-primary-method-select"
                    >
                      <option value="sola_daf">Sola DAF</option>
                      <option value="stripe_card">Stripe Card</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[#111827]">
                      Fallback donations funding method
                    </span>
                    <select
                      value={paymentFallbackDonationFundingMethod}
                      onChange={(event) =>
                        setPaymentFallbackDonationFundingMethod(
                          event.currentTarget.value as DonationFundingMethod,
                        )
                      }
                      className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
                      data-testid="control-plane-payments-sola-fallback-method-select"
                    >
                      <option value="stripe_card">Stripe Card</option>
                    </select>
                  </label>
                  <label className="block">
                    <span className="mb-1 block text-sm font-medium text-[#111827]">
                      Recurring donations funding method
                    </span>
                    <select
                      value={paymentRecurringDonationFundingMethod}
                      onChange={(event) =>
                        setPaymentRecurringDonationFundingMethod(
                          event.currentTarget.value as DonationFundingMethod,
                        )
                      }
                      className="block h-10 w-full rounded-md border border-[#D1D5DB] px-3 text-sm text-[#111827] shadow-sm focus:border-[#2697a2] focus:ring-[#2697a2]"
                      data-testid="control-plane-payments-sola-recurring-method-select"
                    >
                      <option value="stripe_card">Stripe Card</option>
                    </select>
                  </label>
                </div>

                <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#237062]">
                    Sola Runtime Readiness
                  </p>
                  <div className="mt-3">
                    <DetailRow
                      label="Public iFields Key"
                      value={formatBooleanValue(
                        paymentReadiness.sola.hasPublicIfieldsKeyConfigured,
                      )}
                      testId="control-plane-payments-sola-public-key-value"
                    />
                    <DetailRow
                      label="Transaction Key"
                      value={formatBooleanValue(
                        paymentReadiness.sola.hasTransactionKeyConfigured,
                      )}
                      testId="control-plane-payments-sola-transaction-key-value"
                    />
                    <DetailRow
                      label="Webhook PIN"
                      value={formatBooleanValue(
                        paymentReadiness.sola.hasWebhookConfigured,
                      )}
                      testId="control-plane-payments-sola-webhook-value"
                    />
                    <DetailRow
                      label="Primary"
                      value={
                        paymentReadinessWithDonationsFunding
                          ?.donationsFundingReadiness
                          ?.oneTimePrimaryFundingMethod ??
                        paymentReadiness.sola.primaryDonationFundingMethod
                      }
                      testId="control-plane-payments-sola-primary-value"
                    />
                    <DetailRow
                      label="Fallback"
                      value={
                        paymentReadinessWithDonationsFunding
                          ?.donationsFundingReadiness
                          ?.oneTimeFallbackFundingMethod ??
                        paymentReadiness.sola.fallbackDonationFundingMethod
                      }
                      testId="control-plane-payments-sola-fallback-value"
                    />
                    <DetailRow
                      label="Recurring"
                      value={
                        paymentReadinessWithDonationsFunding
                          ?.donationsFundingReadiness
                          ?.recurringFundingMethod ?? 'stripe_card'
                      }
                      testId="control-plane-payments-sola-recurring-value"
                    />
                  </div>
                </div>
              </div>
            </div>

            <PaymentMethodStatusCard generalRoute={generalRoute} compact={false} />
            <PaymentIssuesPanel issues={paymentIssues} />
          </div>
        </div>
      ) : paymentReadinessQuery.isError ? (
        <p
          className="rounded-lg border border-[#FCA5A5] bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#B91C1C]"
          data-testid="control-plane-payments-load-error"
        >
          Unable to load Stripe payment readiness.
        </p>
      ) : (
        <p
          className="rounded-lg border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-3 text-sm text-text-secondary"
          data-testid="control-plane-payments-loading"
        >
          Loading Stripe payment readiness...
        </p>
      )}
    </Card>
  );
};
